import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { 
  Search, Filter, Phone, Mail, User, DollarSign, FileDown, MapPin, Building2, 
  Palette, Home, Grid3X3, Wrench, UserPlus, ArrowRight, TrendingUp, Eye, Trash2, CheckSquare, Square
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  quoted: 'bg-purple-100 text-purple-700',
  closed_won: 'bg-green-100 text-green-700',
  closed_lost: 'bg-red-100 text-red-700',
};

const getColorName = (hex) => {
  const colorMap = {
    '#C41E3A': 'Red', '#722F37': 'Burgundy', '#1E3A8A': 'Blue',
    '#00CED1': 'Hawaiian Blue', '#166534': 'Green', '#6B7280': 'Gray',
    '#374151': 'Charcoal', '#FFFFFF': 'White', '#1F2937': 'Black',
    '#D4A574': 'Tan', '#78350F': 'Brown', '#9CA3AF': 'Gray',
    '#4B5563': 'Charcoal', '#C4B7A6': 'Clay', 'none': 'None'
  };
  return colorMap[hex] || hex || 'N/A';
};

const getEnclosureText = (value, siding) => {
  if (value === 'enclosed') return `Fully Enclosed - ${siding || 'Horizontal'}`;
  if (value === 'partial') return `Partial - ${siding || 'Horizontal'}`;
  return 'Open';
};

const LeadsPage = () => {
  const { api, hasPermission, user, token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [assigningLead, setAssigningLead] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [sendingToOpportunity, setSendingToOpportunity] = useState(false);
  
  // Selection state for delete functionality
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role_name?.toLowerCase() === 'admin' || hasPermission('perm_roles_read');

  useEffect(() => {
    fetchLeads();
    if (hasPermission('perm_users_read')) {
      fetchUsers();
    }
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads/');
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Only fetch users with Sales Representative or Sales Manager roles
      const response = await api.get('/users/sales');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch sales users:', error);
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await api.put(`/leads/${leadId}`, { status });
      toast.success('Lead status updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const assignLead = async () => {
    if (!assigningLead || !selectedUserId) {
      toast.error('Please select a user to assign');
      return;
    }

    try {
      await api.put(`/leads/${assigningLead.id}`, { assigned_to: selectedUserId });
      toast.success('Lead assigned successfully');
      setShowAssignModal(false);
      setAssigningLead(null);
      setSelectedUserId('');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to assign lead');
    }
  };

  const sendToOpportunity = async () => {
    if (!selectedLead) return;

    setSendingToOpportunity(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/opportunities/from-lead/${selectedLead.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Lead sent to opportunities pipeline');
        setShowOpportunityModal(false);
        setSelectedLead(null);
        fetchLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create opportunity');
      }
    } catch (error) {
      toast.error('Failed to send to opportunity');
    } finally {
      setSendingToOpportunity(false);
    }
  };

  const downloadPdf = async (leadId) => {
    try {
      setDownloadingPdf(leadId);
      const response = await api.get(`/leads/${leadId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FusionBuilderPro_Quote_${leadId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Selection handlers
  const toggleSelectLead = (leadId) => {
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeads.size === filteredLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(filteredLeads.map(l => l.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLeads.size === 0) return;
    
    setDeleting(true);
    try {
      const deletePromises = Array.from(selectedLeads).map(leadId =>
        api.delete(`/leads/${leadId}`)
      );
      
      await Promise.all(deletePromises);
      
      toast.success(`${selectedLeads.size} lead(s) deleted successfully`);
      setSelectedLeads(new Set());
      setShowDeleteModal(false);
      fetchLeads();
    } catch (error) {
      console.error('Failed to delete leads:', error);
      toast.error('Failed to delete some leads');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success('Lead deleted successfully');
      setSelectedLeads(prev => {
        const newSet = new Set(prev);
        newSet.delete(leadId);
        return newSet;
      });
      fetchLeads();
    } catch (error) {
      console.error('Failed to delete lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  const getAssignedUserName = (assignedTo) => {
    if (!assignedTo) return null;
    const assignedUser = users.find(u => u.id === assignedTo);
    return assignedUser?.name || 'Unknown';
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.customer_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Leads</h1>
            <p className="text-gray-500 dark:text-gray-400">Manage your sales leads and assign to team members</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">{filteredLeads.length} leads</span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search leads by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="leads-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="leads-status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="quoted">Quoted</SelectItem>
                <SelectItem value="closed_won">Closed Won</SelectItem>
                <SelectItem value="closed_lost">Closed Lost</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Delete Selected Button */}
            {hasPermission('perm_leads_delete') && selectedLeads.size > 0 && (
              <Button 
                variant="destructive" 
                onClick={() => setShowDeleteModal(true)}
                data-testid="delete-selected-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedLeads.size})
              </Button>
            )}
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm dark:shadow-gray-800/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leads-table">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {hasPermission('perm_leads_delete') && (
                    <th className="py-4 px-4 w-12">
                      <Checkbox
                        checked={filteredLeads.length > 0 && selectedLeads.size === filteredLeads.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                  )}
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Customer</th>
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Contact</th>
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Value</th>
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Status</th>
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Assigned To</th>
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Date</th>
                  {isAdmin && (
                    <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Opportunity</th>
                  )}
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} className={`border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 ${selectedLeads.has(lead.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`} data-testid={`lead-row-${lead.id}`}>
                    {hasPermission('perm_leads_delete') && (
                      <td className="py-4 px-4">
                        <Checkbox
                          checked={selectedLeads.has(lead.id)}
                          onCheckedChange={() => toggleSelectLead(lead.id)}
                          data-testid={`select-lead-${lead.id}`}
                        />
                      </td>
                    )}
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 dark:text-gray-100">{lead.customer_name}</p>
                          {lead.customer_zip && (
                            <p className="text-xs text-gray-400 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {lead.customer_zip}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                          <Mail className="w-4 h-4" />
                          {lead.customer_email}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                          <Phone className="w-4 h-4" />
                          {lead.customer_phone}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="font-semibold text-[#C41E3A]">
                        ${lead.total_price?.toLocaleString() || '0'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      {hasPermission('perm_leads_update') ? (
                        <Select
                          value={lead.status}
                          onValueChange={(value) => updateLeadStatus(lead.id, value)}
                        >
                          <SelectTrigger className={`w-[140px] ${statusColors[lead.status]}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="quoted">Quoted</SelectItem>
                            <SelectItem value="closed_won">Closed Won</SelectItem>
                            <SelectItem value="closed_lost">Closed Lost</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[lead.status]}`}>
                          {lead.status?.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        {lead.assigned_to ? (
                          <span className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                            {getAssignedUserName(lead.assigned_to)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">Unassigned</span>
                        )}
                        {hasPermission('perm_leads_update') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setAssigningLead(lead);
                              setSelectedUserId(lead.assigned_to || '');
                              setShowAssignModal(true);
                            }}
                            title="Assign to user"
                            data-testid={`assign-lead-${lead.id}`}
                          >
                            <UserPlus className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-500 text-sm">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    {isAdmin && (
                      <td className="py-4 px-6">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedLead(lead);
                            setShowOpportunityModal(true);
                          }}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                          data-testid={`send-to-opp-${lead.id}`}
                        >
                          <TrendingUp className="w-4 h-4 mr-1" />
                          Send to Pipeline
                        </Button>
                      </td>
                    )}
                    <td className="py-4 px-6">
                      <div className="flex gap-2">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" onClick={() => setSelectedLead(lead)} data-testid={`view-lead-${lead.id}`}>
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-[#C41E3A]" />
                                Lead Details & Building Configuration
                              </DialogTitle>
                            </DialogHeader>
                            {selectedLead && (
                              <div className="space-y-6">
                                {/* Customer Info */}
                                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                  <h3 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                                    <User className="w-4 h-4" /> Customer Information
                                  </h3>
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400">Name</p>
                                      <p className="font-medium text-gray-900 dark:text-white">{selectedLead.customer_name}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400">Email</p>
                                      <p className="font-medium text-gray-900 dark:text-white">{selectedLead.customer_email}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400">Phone</p>
                                      <p className="font-medium text-gray-900 dark:text-white">{selectedLead.customer_phone}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400">Zip Code</p>
                                      <p className="font-medium text-gray-900 dark:text-white">{selectedLead.customer_zip || 'N/A'}</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Building Configuration */}
                                {selectedLead.configuration && (
                                  <>
                                    {/* Basic Info */}
                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                      <h3 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                                        <Building2 className="w-4 h-4" /> Building Configuration
                                      </h3>
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                          <p className="text-gray-500 dark:text-gray-400">Style</p>
                                          <p className="font-medium text-gray-900 dark:text-white capitalize">{selectedLead.configuration.style?.replace('_', ' ') || 'Carport'}</p>
                                        </div>
                                        <div>
                                          <p className="text-gray-500 dark:text-gray-400">Base Size</p>
                                          <p className="font-medium text-gray-900 dark:text-white">{selectedLead.configuration.width || 20}' × {selectedLead.configuration.length || 21}'</p>
                                        </div>
                                        <div>
                                          <p className="text-gray-500 dark:text-gray-400">Leg Height</p>
                                          <p className="font-medium text-gray-900 dark:text-white">{selectedLead.configuration.legHeight || 8}'</p>
                                        </div>
                                        <div>
                                          <p className="text-gray-500 dark:text-gray-400">Installation Surface</p>
                                          <p className="font-medium text-gray-900 dark:text-white capitalize">{selectedLead.configuration.installationSurface || 'Soil'}</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Colors */}
                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                      <h3 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                                        <Palette className="w-4 h-4" /> Colors
                                      </h3>
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div className="flex items-center gap-2">
                                          <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: selectedLead.configuration.roofColor || '#C41E3A' }}></div>
                                          <div>
                                            <p className="text-gray-500 dark:text-gray-400">Roof</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{getColorName(selectedLead.configuration.roofColor)}</p>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: selectedLead.configuration.trimColor || '#FFFFFF' }}></div>
                                          <div>
                                            <p className="text-gray-500 dark:text-gray-400">Trim</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{getColorName(selectedLead.configuration.trimColor)}</p>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: selectedLead.configuration.sideColor || '#FFFFFF' }}></div>
                                          <div>
                                            <p className="text-gray-500 dark:text-gray-400">Side/Wall</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{getColorName(selectedLead.configuration.sideColor)}</p>
                                          </div>
                                        </div>
                                        {selectedLead.configuration.wainscotColor && selectedLead.configuration.wainscotColor !== 'none' && (
                                          <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: selectedLead.configuration.wainscotColor }}></div>
                                            <div>
                                              <p className="text-gray-500 dark:text-gray-400">Wainscot (36")</p>
                                              <p className="font-medium text-gray-900 dark:text-white">{getColorName(selectedLead.configuration.wainscotColor)}</p>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Roof Details */}
                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                      <h3 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                                        <Home className="w-4 h-4" /> Roof Details
                                      </h3>
                                      <div className="grid grid-cols-3 gap-4 text-sm">
                                        <div>
                                          <p className="text-gray-500 dark:text-gray-400">Roof Style</p>
                                          <p className="font-medium text-gray-900 dark:text-white capitalize">{selectedLead.configuration.roofStyle || 'Regular'}</p>
                                        </div>
                                        <div>
                                          <p className="text-gray-500 dark:text-gray-400">Roof Pitch</p>
                                          <p className="font-medium text-gray-900 dark:text-white">{selectedLead.configuration.roofPitch || 3}/12</p>
                                        </div>
                                        <div>
                                          <p className="text-gray-500 dark:text-gray-400">Overhang</p>
                                          <p className="font-medium text-gray-900 dark:text-white">6"</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Enclosures */}
                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                      <h3 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                                        <Grid3X3 className="w-4 h-4" /> Enclosures
                                      </h3>
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                          <p className="text-gray-500 dark:text-gray-400">Front Side</p>
                                          <p className="font-medium text-gray-900 dark:text-white">{getEnclosureText(selectedLead.configuration.frontSide, selectedLead.configuration.sidingOrientation)}</p>
                                        </div>
                                        <div>
                                          <p className="text-gray-500 dark:text-gray-400">Back Side</p>
                                          <p className="font-medium text-gray-900 dark:text-white">{getEnclosureText(selectedLead.configuration.backSide, selectedLead.configuration.sidingOrientation)}</p>
                                        </div>
                                        <div>
                                          <p className="text-gray-500 dark:text-gray-400">Left End</p>
                                          <p className="font-medium text-gray-900 dark:text-white">{getEnclosureText(selectedLead.configuration.leftEnd, selectedLead.configuration.sidingOrientation)}</p>
                                        </div>
                                        <div>
                                          <p className="text-gray-500 dark:text-gray-400">Right End</p>
                                          <p className="font-medium text-gray-900 dark:text-white">{getEnclosureText(selectedLead.configuration.rightEnd, selectedLead.configuration.sidingOrientation)}</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Doors & Windows */}
                                    {(selectedLead.configuration.garageDoors?.length > 0 || 
                                      selectedLead.configuration.walkDoors?.length > 0 || 
                                      selectedLead.configuration.windows?.length > 0) && (
                                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                        <h3 className="font-semibold text-gray-800 dark:text-white mb-3">Doors & Windows</h3>
                                        <div className="space-y-2 text-sm">
                                          {selectedLead.configuration.garageDoors?.map((door, idx) => (
                                            <p key={`gd-${idx}`} className="flex justify-between text-gray-900 dark:text-white">
                                              <span>Garage Door ({door.width}'×{door.height}')</span>
                                              <span className="text-gray-500 dark:text-gray-400">{door.wall?.replace('_', ' ')}</span>
                                            </p>
                                          ))}
                                          {selectedLead.configuration.walkDoors?.map((door, idx) => (
                                            <p key={`wd-${idx}`} className="flex justify-between text-gray-900 dark:text-white">
                                              <span>Walk-in Door (36"×80")</span>
                                              <span className="text-gray-500 dark:text-gray-400">{door.wall?.replace('_', ' ')}</span>
                                            </p>
                                          ))}
                                          {selectedLead.configuration.windows?.map((win, idx) => (
                                            <p key={`w-${idx}`} className="flex justify-between text-gray-900 dark:text-white">
                                              <span>Window ({win.width}'×{win.height}')</span>
                                              <span className="text-gray-500 dark:text-gray-400">{win.wall?.replace('_', ' ')}</span>
                                            </p>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Services */}
                                    {selectedLead.configuration.services && Object.values(selectedLead.configuration.services).some(v => v) && (
                                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                        <h3 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                                          <Wrench className="w-4 h-4" /> Selected Services
                                        </h3>
                                        <div className="space-y-1 text-sm text-gray-900 dark:text-white">
                                          {selectedLead.configuration.services.buildOverExisting && <p>• Build Over Existing Structure</p>}
                                          {selectedLead.configuration.services.cutLegsOnSite && <p>• Cut Legs on Site</p>}
                                          {selectedLead.configuration.services.installOverExisting && <p>• Install Over Existing Structure</p>}
                                          {selectedLead.configuration.services.installOnDeck && <p>• Install on Deck or Dock</p>}
                                          {selectedLead.configuration.services.installOnWall && <p>• Install on Wall (Up to 3')</p>}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}

                                {/* Pricing */}
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                  <div className="flex justify-between items-center">
                                    <span className="font-semibold text-gray-800 dark:text-gray-100">Total Quote Value</span>
                                    <span className="text-2xl font-bold text-[#C41E3A]">
                                      ${selectedLead.total_price?.toLocaleString() || '0'}
                                    </span>
                                  </div>
                                </div>

                                {/* Notes */}
                                {selectedLead.notes && (
                                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <p className="text-sm font-medium text-yellow-800">Customer Notes:</p>
                                    <p className="text-sm text-yellow-700 mt-1">{selectedLead.notes}</p>
                                  </div>
                                )}

                                {/* Action Buttons */}
                                <div className="flex gap-3">
                                  <Button 
                                    onClick={() => downloadPdf(selectedLead.id)}
                                    disabled={downloadingPdf === selectedLead.id}
                                    className="flex-1 bg-[#C41E3A] hover:bg-[#a01830] text-white"
                                  >
                                    <FileDown className="w-4 h-4 mr-2" />
                                    {downloadingPdf === selectedLead.id ? 'Generating PDF...' : 'Download Quote PDF'}
                                  </Button>
                                  {isAdmin && (
                                    <Button 
                                      onClick={() => {
                                        setShowOpportunityModal(true);
                                      }}
                                      variant="outline"
                                      className="text-green-600 border-green-300 hover:bg-green-50"
                                    >
                                      <TrendingUp className="w-4 h-4 mr-2" />
                                      Send to Pipeline
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => downloadPdf(lead.id)}
                          disabled={downloadingPdf === lead.id}
                          title="Download PDF"
                        >
                          <FileDown className={`w-4 h-4 ${downloadingPdf === lead.id ? 'animate-pulse' : ''}`} />
                        </Button>
                        {hasPermission('perm_leads_delete') && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteSingle(lead.id)}
                            title="Delete lead"
                            data-testid={`delete-lead-${lead.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-600"></div>
            </div>
          )}
          {!loading && filteredLeads.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No leads found
            </div>
          )}
        </div>
      </div>

      {/* Assign Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Lead</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Assign <strong>{assigningLead?.customer_name}</strong> to a team member:
            </p>
            <Select value={selectedUserId || "unassigned"} onValueChange={(val) => setSelectedUserId(val === "unassigned" ? "" : val)}>
              <SelectTrigger data-testid="assign-user-select">
                <SelectValue placeholder="Select team member..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button onClick={assignLead} data-testid="confirm-assign-btn">
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to Opportunity Modal */}
      <Dialog open={showOpportunityModal} onOpenChange={setShowOpportunityModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Send to Sales Pipeline
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-green-800">
                This will create an opportunity in the sales pipeline from this lead:
              </p>
              <div className="mt-3 space-y-1">
                <p className="font-medium">{selectedLead?.customer_name}</p>
                <p className="text-sm text-green-700">${selectedLead?.total_price?.toLocaleString()}</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              The lead status will be updated to "Contacted" and you can track the opportunity in the CRM Pipeline.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpportunityModal(false)}>Cancel</Button>
            <Button 
              onClick={sendToOpportunity} 
              disabled={sendingToOpportunity}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-send-opp-btn"
            >
              {sendingToOpportunity ? 'Creating...' : 'Create Opportunity'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Leads
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800">
                Are you sure you want to delete <strong>{selectedLeads.size}</strong> lead(s)?
              </p>
              <p className="text-sm text-red-600 mt-2">
                This action cannot be undone.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteSelected} 
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : `Delete ${selectedLeads.size} Lead(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default LeadsPage;
